import datapoints from '../../datapoints';
import type { FullComposeViewState } from 'owa-calendar-compose-types';
import isNewEvent from '../../selectors/isNewEvent';
import shouldShowTimeSuggestions from '../../selectors/shouldShowTimeSuggestions';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import cloneEvent from '../../utils/cloneEvent';
import createComposeViewState from '../../utils/createComposeViewState';
import initFreeBusyCache from '../../utils/initFreeBusyCache';
import { clearPendingInlineImages } from '../../utils/pendingInlineImages';
import { onShowFullCompose } from '../formOpenCloseNotificationActions';
import { isConnectedAccount } from 'owa-accounts-store';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { dismissUndoNotification } from 'owa-calendar-actions';
import { lazyInitializeCalendarChannelPicker } from 'owa-calendar-channel-picker';
import { initializeLocationMetrics } from '../../mutators/eventMetricsMutators';
import isEntrySourceUndo from '../../utils/isEntrySourceUndo';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import {
    FullComposeFormViewType,
    FULL_COMPOSE_FORM_TYPE,
    QUICK_COMPOSE_FORM_TYPE,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import initAttachmentWell from 'owa-calendar-meeting-forms-common-actions/lib/actions/initAttachmentWell';
import {
    convertToSchedulingSelectionItem,
    initializeSchedulingGrid,
} from 'owa-calendar-scheduling-grid';
import { KnownItemTypes } from 'owa-calendar-surface-dategrid';
import { initializeTimeSuggestions, setCalendarComponent } from 'owa-calendar-time-suggestions';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { owaDate, startOfDay } from 'owa-datetime';
import initializeLocationFeedbackManagerState from 'owa-feedback-manager-actions/lib/actions/initializeLocationFeedbackManagerState';
import entityFeedbackStore from 'owa-feedback-manager-store/lib/store/locationFeedbackManagerStore';
import { getGuid } from 'owa-guid';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { getOpxHostApi } from 'owa-opx';
import { lazyInitializeCache } from 'owa-recipient-cache';
import { lazyShowRoomPanel } from 'owa-room-selection';
import { getUserTimeZone, isConsumer } from 'owa-session-store';
import {
    FullComposeEntrySourceType,
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    logComposeFormOpenedTimes,
    initializeTimeSuggestionMetricsInfo,
    verifyEventThenSave,
    presentDiscardClosureConfirmation,
    initializeCalendarEvents,
} from 'owa-calendar-compose-form-actions';
import getCalendarComponent from '../../utils/getCalendarComponent';
import { getId } from '@fluentui/react/lib/Utilities';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';

/**
 * Show full compose in popout window
 * @param {FullComposeViewState} fullComposeViewState The view state passed from parent window
 * @param {CalendarEvent} event The event object passed from parent window
 * @param {CalendarEvent} originalEvent The original event object passed from parent window
 */
let showFullComposeInPopOut = createLegacyActionWithDatapoint('showFullComposeInPopOut')(
    function showFullComposeInPopOut(
        fullComposeViewState: FullComposeViewState,
        event: CalendarEvent,
        originalEvent: CalendarEvent
    ) {
        let store = getComposeStore();
        store.event = event;
        store.originalEvent = originalEvent;
        internalShowFullCompose(fullComposeViewState, 'PopOut');
    }
);

export { showFullComposeInPopOut };

/**
 * Shows the full compose form by initializng the full compose form
 * @param {boolean} isCharmExplicitlySet If true, charm suggestions will not be provided until charm is unset.
 * @param {FullComposeEntrySourceType} entrySource
 * @param {extractionSourceId} A specific entity extraction id associates with the action source
 * @param {internetMessageId} An unique id for a message
 */
export default createLegacyActionWithDatapoint(
    'showFullCompose',
    datapoints.showFullCompose
)(async function showFullCompose(
    isCharmExplicitlySet: boolean,
    entrySource: FullComposeEntrySourceType,
    viewType: FullComposeFormViewType,
    extractionSourceId?: string | null,
    internetMessageId?: string,
    createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps
) {
    const isUndo = isEntrySourceUndo(entrySource);
    const store = getComposeStore();

    const formId = getId('CalendarCompose_');
    store.formId = formId;

    // If the form's entry source is undo, the notification is dismissed when the undo button is
    // selected. In all other cases, we dismiss the notification manually and reset the store.
    if (!isUndo) {
        const event = store.event;
        dismissUndoNotification();

        // dismissing the undo notification resets the store, so we need to reinitialize it
        if (!store.event) {
            initializeCalendarEvents(event);
        }
    }

    store.metricsInfo = {
        ...store.metricsInfo,
    };

    // TODO: Refactor to remove metricsInfo.formSessionId and only have one formId per form
    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/112835
    if (!store.metricsInfo.formSessionId) {
        store.metricsInfo.formSessionId = getGuid();
    }

    // Take a snapshot of the final event in quick compose
    if (entrySource === 'QuickCompose') {
        store.metricsInfo.quickComposeFinalEvent = cloneEvent(store.event);
    }

    if (entrySource === 'Mail_SmartReplyWithMeeting') {
        store.metricsInfo.extractionSourceId = extractionSourceId;
    }

    let viewState: FullComposeViewState = createComposeViewState(
        store.event,
        isCharmExplicitlySet,
        viewType,
        isAcceptOrReviewPNTEntrySource(entrySource) /* isAcceptPNT */,
        extractionSourceId,
        internetMessageId,
        store.uiState
            ?.collabSpaceViewStateId /* only valid when opening full compose from quick compose */,
        createCollabSpaceOnInitProps
    );
    await internalShowFullCompose(viewState, entrySource);
});

export async function internalShowFullCompose(
    viewState: FullComposeViewState,
    entrySource: FullComposeEntrySourceType
) {
    if (isHostAppFeatureEnabled('composeFormSaveCloseHandlers')) {
        // if opx scenario, then register save and close handlers
        const opxHostApi = getOpxHostApi();
        if (opxHostApi.registerFormSaveHandler) {
            opxHostApi.registerFormSaveHandler(() => verifyEventThenSave());
        }

        if (opxHostApi.registerFormCloseHandler) {
            opxHostApi.registerFormCloseHandler(() => presentDiscardClosureConfirmation());
        }
    }
    const store = getComposeStore();
    const event = store.event;

    // if viewState is null, initialize it
    if (!viewState) {
        viewState = createComposeViewState(
            event,
            false /* isCharmExplicitlySet */,
            'PopOut' /* viewType */,
            false /* isAcceptPNT */,
            null /* extractionSourceId */
        );
    }

    // Normalize time zones in the event, so the whole form runs out of Start.tz and End.tz.
    const userTimeZone = getUserTimeZone();
    event.StartTimeZoneId = event.StartTimeZoneId || userTimeZone;
    event.EndTimeZoneId = event.EndTimeZoneId || userTimeZone;
    event.Start = owaDate(event.StartTimeZoneId, event.Start);
    event.End = owaDate(event.EndTimeZoneId, event.End);

    // Inline image plugin requires the attachment well to be initialized
    await initAttachmentWell(event, false /* reInitialize */, viewState, false /* isReadOnly */);

    // Clear pending inline images added by previous compose session if any
    clearPendingInlineImages();

    logComposeFormOpenedTimes();
    const previousOpenedForm = store.openedForm;
    store.openedForm = FULL_COMPOSE_FORM_TYPE;
    store.uiState = viewState;

    store.metricsInfo.fullComposeEntrySource = entrySource;
    if (!isEntrySourceUndo(entrySource) && entrySource != FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE) {
        // If it is not the undo scenario and it's not the transition from quick compose scenario, then we want to not log the quickComposeEntrySource
        // that was set in the previous session
        store.metricsInfo.quickComposeEntrySource = undefined;
    }

    // initialize entityFeedbackStore if needed
    if (!entityFeedbackStore.feedbackManagerState) {
        initializeLocationFeedbackManagerState();
    }

    // initialize FreeBusy Cache if needed
    initFreeBusyCache(event, store.openedForm);

    initializeSchedulingGrid.importAndExecute(
        viewState.schedulingGridScenarioId,
        [event.ParentFolderId.Id],
        startOfDay(event.Start),
        convertToSchedulingSelectionItem(event, KnownItemTypes.suggestedItems),
        true, // showSelectionItem
        true //disableTabbableSurfaceItems
    );
    if (shouldShowTimeSuggestions()) {
        if (previousOpenedForm != QUICK_COMPOSE_FORM_TYPE) {
            // If the suggestions flight is enabled, only then import the suggestions code
            initializeTimeSuggestions.importAndExecute(
                event,
                viewState.shouldAdjustStartTimeEndTime,
                getCalendarComponent() /* calendarComponent */
            );
            initializeTimeSuggestionMetricsInfo();
        } else {
            setCalendarComponent(getCalendarComponent());
        }
    }

    const userIdentity = event.ParentFolderId.mailboxInfo.userIdentity;
    if (event.ParentFolderId.mailboxInfo) {
        lazyShowRoomPanel.importAndExecute(
            event,
            isConsumer(userIdentity),
            false /** isDeepLink */
        );
    }

    // if this is not a transition from quick compose then
    // initialize the location metrics and people cache
    if (entrySource != FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE) {
        initializeLocationMetrics();
        if (!isConnectedAccount(userIdentity)) {
            lazyInitializeCache.importAndExecute();
        }
    }

    if (isHostAppFeatureEnabled('navigateToView')) {
        // After form is open, then let the host know that the view has changed to ComposeForm
        const opxHostApi = getOpxHostApi();
        if (opxHostApi.navigateToView) {
            opxHostApi.navigateToView(
                isNewEvent() ? 'ComposeForm' : 'EditForm',
                isEventAMeeting(event) ? 'Send' : 'Save',
                event
            );
        }
    }

    lazyInitializeCalendarChannelPicker.importAndExecute(event);

    // Notify listeners that full compose is ready, must be last call in this method
    onShowFullCompose();
}

function isAcceptOrReviewPNTEntrySource(entrySource: FullComposeEntrySourceType) {
    return (
        entrySource == 'Mail_AcceptPNT' ||
        entrySource == 'PeekReviewPNT' ||
        entrySource == 'TimePanelReviewPNT' ||
        entrySource == 'ReviewPNT'
    );
}
